import { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import Card from '@/components/ui/Card'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { faqApi } from '@/services/faqApiService'
import { getLocalizedValue } from '@sakeena/shared'
import { useTranslation } from 'react-i18next'
import { useCurrentLanguage } from '@/utils/contentVisibility'

// Helper to normalize FAQ data (handle both camelCase and PascalCase)
const normalizeFAQ = (faq: any): any => ({
  id: faq.id || faq.Id || '',
  question: faq.question || faq.Question || '',
  answer: faq.answer || faq.Answer || '',
  order: faq.order ?? faq.Order ?? 0,
  isActive: faq.isActive ?? faq.IsActive ?? true,
})

export default function FAQsTiles() {
  const { t } = useTranslation()
  const safeT = t || ((key: string, def: string) => def || key)
  const currentLang = useCurrentLanguage()
  const [faqs, setFaqs] = useState<any[]>([])
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await faqApi.getActiveFAQs()
        const normalized = (data || []).map(normalizeFAQ)
        // Show all active FAQs; display uses getLocalizedValue so fallback language is shown when current lang missing
        const active = normalized.filter((f) => f.isActive !== false)
        setFaqs(active)
      } catch (err) {
        console.error('Failed to fetch FAQs:', err)
        setError(safeT('doubtResponses.loadingError', 'فشل تحميل الأسئلة الشائعة'))
      } finally {
        setLoading(false)
      }
    }

    fetchFAQs()
  }, [safeT, currentLang])

  const toggleFAQ = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((faqId) => faqId !== id) : [...prev, id]
    )
  }

  if (loading) {
    return (
      <section className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </section>
    )
  }

  if (faqs.length === 0) {
    return null
  }

  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {safeT('home.faqs.title', 'أسئلة شائعة عن الإسلام')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {safeT('home.faqs.subtitle', 'إجابات على الأسئلة الأكثر شيوعاً')}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {faqs.map((faq) => {
            const isExpanded = expandedIds.includes(faq.id)
            return (
              <Card
                key={faq.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => toggleFAQ(faq.id)}
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 flex-1 pr-4">
                    {getLocalizedValue(faq.question, currentLang)}
                  </h3>
                  <button
                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-shrink-0"
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {isExpanded && (
                  <p className="text-gray-600 dark:text-gray-300 mt-4 leading-relaxed">
                    {getLocalizedValue(faq.answer, currentLang)}
                  </p>
                )}
              </Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}
